import { Loading, RetryErrorMessage, Subscription } from "@/components";
import { PageLayout } from "@/components/layouts/PageLayout";
import { Button } from "@/components/ui/button";
import { Form, FormField } from "@/components/ui/form";
import { RadioGroup } from "@/components/ui/radio-group";
import { useUser } from "@/firebase";
import type { SubscriptionsQueryParamsSchema } from "@/routes/register/subscriptions";
import { useRegister } from "@/services/context/register";
import { useScrollToTop } from "@/services/hooks";
import { subscriptionsDataQuery } from "@/services/queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import * as z from "zod";

const subscriptionsSchema = z.object({
    subscription_id: z.string(),
});

function Subscriptions() {
    useScrollToTop();
    // Get initial data from registry context
    const { registryData, setRegistryData } = useRegister();

    const { user } = useUser();

    let {
        promo_code = registryData?.promo_code,
        referrer_code = registryData?.referrer_code,
        subs_id,
        ref,
    } = useSearch({
        strict: false,
    }) as SubscriptionsQueryParamsSchema;

    // If both promo_code and referrer_code are provided. Only use promo_code
    if (promo_code && referrer_code) {
        referrer_code = undefined;
    }

    const {
        data: response,
        isLoading,
        isError,
        error,
        refetch,
    } = subscriptionsDataQuery({
        promo_code,
        referrer_code,
        partnership_data: registryData?.partnership_data
            ? JSON.stringify(registryData?.partnership_data)
            : undefined,
    });
    const data = response?.data;

    const navigate = useNavigate();

    const form = useForm<z.infer<typeof subscriptionsSchema>>({
        resolver: zodResolver(subscriptionsSchema),
        defaultValues: {
            subscription_id: registryData?.subscription_id ?? String(subs_id),
        },
    });

    const selectedSubscription = form.watch("subscription_id");

    function onSubmit({
        subscription_id,
    }: z.infer<typeof subscriptionsSchema>) {
        // Don´t save referrer_user_id if promo code is provided
        const referrer_user_id =
            !promo_code && data?.referrer?.referrer_user_id
                ? String(data?.referrer?.referrer_user_id)
                : undefined;
        setRegistryData({
            ...registryData,
            promo_code,
            subscription_id,
            referrer_code,
            referrer_user_id,
        });
        navigate({
            to: "/register/shipping-methods",
            search: {
                ref,
            },
        });
    }

    const isNextStepDisable = () => {
        if (registryData?.subscription_status === "SUBSCRIPTION_PENDING") {
            return false;
        }
        if (
            (registryData?.subscription_id === undefined &&
                Number.isNaN(Number(selectedSubscription))) ||
            user?.email != null
        ) {
            return true;
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return <Loading message="Cargando suscripciones Maifud..." />;
        }

        if (isError) {
            return (
                <RetryErrorMessage
                    title={
                        "No se pudo obtener la información de las suscripciones. Por favor intenta de nuevo."
                    }
                    message={error.message}
                    retry={refetch}
                    error={error}
                />
            );
        }

        if (data?.subscriptions) {
            return (
                <>
                    {registryData?.subscription_status ===
                        "SUBSCRIPTION_PENDING" && (
                        <div className="text-center p-4 rounded-md border bg-orange-100/50 text-primary font-semibold">
                            <div className="space-x-1">
                                <span>🔔</span>
                                <span>
                                    Tienes una suscripción pendiente por
                                    terminar
                                </span>
                                <span>🔔</span>
                            </div>
                            <div className="pt-2">
                                <Button>
                                    <Link to="/register/confirmation">
                                        Continuar registro
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    )}
                    {data.promo_code && (
                        <div>
                            <p className="text-center text-sm text-muted-foreground">
                                Tenemos un descuento para ti
                            </p>
                            <div className="text-center p-4 rounded-md border bg-orange-100/50 text-primary font-semibold space-x-1">
                                <span>🎉 </span>
                                <span>{data.promo_code.disclaimer}</span>
                                <span>🎉 </span>
                            </div>
                        </div>
                    )}
                    {data.partnership && (
                        <div>
                            <div className="text-center p-4 rounded-md border bg-orange-100/50 text-primary font-semibold space-x-1">
                                <span>🎉 </span>
                                <span>
                                    {data?.partnership.partnership_description}
                                </span>
                                <span>🎉 </span>
                            </div>
                        </div>
                    )}
                    {data.referrer && (
                        <div className="text-center p-4 rounded-md border bg-orange-100/50 text-primary font-semibold space-x-1">
                            <span>🎉 </span>
                            <span>
                                <strong>{data.referrer.referrer_name}</strong>{" "}
                                te regala un envío gratis.
                            </span>
                            <span>🎉 </span>
                        </div>
                    )}
                    {user?.email &&
                        registryData?.subscription_status !==
                            "SUBSCRIPTION_PENDING" && (
                            <div className="text-center p-4 rounded-md border bg-orange-100/50 space-x-1">
                                <span>
                                    Ya tienes una suscripción en Maifud.{" "}
                                    <Link
                                        to="/user/subscription"
                                        className="text-primary underline"
                                    >
                                        Puedes acceder a tu cuenta haciendo
                                        click aquí
                                    </Link>
                                </span>
                            </div>
                        )}
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="relative"
                        >
                            <FormField
                                control={form.control}
                                name="subscription_id"
                                render={({ field }) => (
                                    <RadioGroup
                                        className="grid grid-cols-2 gap-3 pb-24 sm:gap-4 md:pb-4 lg:grid-cols-4"
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                    >
                                        {data.subscriptions.map((item) => {
                                            return (
                                                <Subscription
                                                    key={item.id}
                                                    subscription={item}
                                                />
                                            );
                                        })}
                                    </RadioGroup>
                                )}
                            />
                            <div className="fixed bottom-0 left-0 right-0 z-40 flex w-full justify-end border-t bg-background/95 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] shadow-[0_-8px_20px_rgba(15,23,42,0.08)] backdrop-blur md:relative md:border-0 md:bg-transparent md:px-0 md:pb-4 md:shadow-none">
                                <Button
                                    type="submit"
                                    className="w-full sm:w-fit"
                                    disabled={isNextStepDisable()}
                                >
                                    Siguiente
                                </Button>
                            </div>
                        </form>
                    </Form>
                </>
            );
        }
    };

    return (
        <PageLayout
            title="Escoge la caja que más te guste"
            description="Esta va a ser tu primera caja. Podrás cambiar el tipo de caja desde tu cuenta"
            progress={10}
        >
            {renderContent()}
        </PageLayout>
    );
}

export default Subscriptions;
